import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';

export default function ModernMultiSelect({ placeholder = 'Select', value = [], onChange, options = [], width = 'w-56', height = 'w-8', isDisabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedValues = Array.isArray(value) ? value : [];
  const safeOptions = Array.isArray(options) ? options : [];

  const filteredOptions = useMemo(() => {
    return safeOptions.filter(option => option.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [safeOptions, searchTerm]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleToggleOption = (option) => {
    if (!onChange) return;
    const exists = selectedValues.includes(option);
    const next = exists ? selectedValues.filter(v => v !== option) : [...selectedValues, option];
    onChange(next);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        try { searchInputRef.current.focus(); } catch (_) {}
      }, 100);
    }
  }, [isOpen]);

  const displayText = selectedValues.length > 0 ? `${selectedValues.length} selected` : placeholder;

  return (
    <div className={`relative ${width}`} ref={dropdownRef}>
      <button
        type="button"
        className={`flex items-center justify-between w-full px-2 py-1.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-md shadow-sm focus:outline-none global-input ${height}`}
        onClick={toggleDropdown}
        disabled={isDisabled}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          {safeOptions.length > 5 && (
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search options..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          )}
          <ul className="py-1 overflow-auto text-sm max-h-60">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const checked = selectedValues.includes(option);
                return (
                  <li
                    key={index}
                    className={`px-3 py-2 cursor-pointer hover:bg-blue-50 flex items-center justify-between ${checked ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                    onClick={() => handleToggleOption(option)}
                  >
                    <span>{option}</span>
                    {checked && <Check className="w-4 h-4 text-blue-600" />}
                  </li>
                );
              })
            ) : (
              <li className="px-4 py-2 text-gray-500 text-center">No options found</li>
            )}
          </ul>
        </div>
      )}

      {selectedValues.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {selectedValues.map((v, i) => (
            <span key={`${v}-${i}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200">
              {v}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


